/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { parse } from "node-html-parser"
import sizeOf from "image-size"
import { makeObservable, observable, computed } from "mobx"
import { htmlToTextTransform } from "@lib/html"
import { InstagramSerializer } from "@serializers/instagram"
import { isServer } from "@lib/isomorphic"

import { BaseModel } from "./base.model"

import type { AxiosError } from "axios"
import type {
  Category,
  Element,
  PostModel,
  ArticleBannerStructure,
} from "@serializers/article"
import type { Localization } from "@serializers/localization"
import type { YoutubeVideoModel, PromoBanner } from "@providers"
import type { BranchLinks } from "@serializers/branch-links"
import type { TikTokModel } from "@serializers/tiktok"

const BRANCH_LINKS_DEFAULT = Object.freeze({
  ru: {},
  en: {},
  fr: {},
  de: {},
  zh: {},
  es: {},
})

const PAGE_SIZE = 6

export class BlogModel extends BaseModel {
  // @ts-ignore
  constructor(params) {
    super(params)
    makeObservable(this)
  }

  @observable templates: Localization[] = []

  @observable editorChoice = null as PostModel[] | null

  @observable posts = [] as PostModel[]
  @observable pagination = {
    page: 0,
    pageSize: 0,
    pageCount: 0,
    rowCount: 0,
  }
  @observable postDetails = null as PostModel | null

  @computed get isAmp() {
    try {
      if (isServer()) {
        return new URL(this.Models.GlobalModel.firstServerUrl).searchParams.has(
          "amp",
        )
      }

      return new URL(window.location.href).searchParams.has("amp")
    } catch (e) {
      return false
      console.log(e) // eslint-disable-line
    }
  }

  @computed get existsGridPosts() {
    return this.posts.length > 0
  }

  @computed get postsChunks() {
    const chunkLength = this.pagination.pageSize || PAGE_SIZE
    return this.posts.reduce((result, _, index, postsArray) => {
      if (index % chunkLength === 0) {
        result.push(postsArray.slice(index, index + chunkLength))
      }
      return result
    }, [] as PostModel[][])
  }

  @computed get category() {
    const category = this.categories?.find(
      ({ slug, lang }) =>
        slug === this.categorySlug && this.Models.GlobalModel.lang === lang,
    )

    return category || null
  }

  @computed get branch_links() {
    return this[
      this.Models.GlobalModel.isPureDating
        ? "branchLinksDating"
        : "branchLinksJournal"
    ][this.Models.GlobalModel.lang]
  }

  @computed get articleBanner() {
    return this.postDetails?.banner ?? this.defaultArticlesBanner ?? null
  }

  BANNER_LINK = "https://pureapp.onelink.me/M5p9/zh48vlee"

  @observable youtube = [] as YoutubeVideoModel[]
  @observable enabledYoutube = false

  @observable instagram = [] as ReturnType<typeof InstagramSerializer>[]
  @observable enabledInstagram = false

  @observable tiktok = [] as TikTokModel[]

  @observable categories = [] as Category[]
  @observable categorySlug = ""
  @observable relatedCategoriesBySlug = [] as Category[]
  @observable relatedPostsBySlug = [] as PostModel[]
  @observable recommendedPosts = [] as PostModel[]
  @observable branchLinksJournal = BRANCH_LINKS_DEFAULT as BranchLinks
  @observable branchLinksDating = BRANCH_LINKS_DEFAULT as BranchLinks

  @observable promoBanner = null as PromoBanner | null

  @observable defaultArticlesBanner = null as ArticleBannerStructure | null

  hydrate = (data: unknown) => {
    // @ts-ignore
    this.applyData({ editorChoice: data?.editorChoice })
    // @ts-ignore
    this.applyData({ posts: data?.posts ?? [] })
    this.applyData({
      // @ts-ignore
      pagination: data?.pagination ?? {
        page: 0,
        pageSize: 0,
        pageCount: 0,
        rowCount: 0,
      },
    })
    // @ts-ignore
    this.applyData({ postDetails: data?.postDetails })
    // @ts-ignore
    this.applyData({ templates: data?.templates })
    // @ts-ignore
    this.applyData({ enabledYoutube: data?.enabledYoutube })
    this.applyData({
      // @ts-ignore
      instagram: data?.instagram,
      // @ts-ignore
      enabledInstagram: data?.enabledInstagram,
    })
    // @ts-ignore
    this.applyData({ tiktok: data?.tiktok })
    // @ts-ignore
    this.applyData({ categories: data?.categories })
    // @ts-ignore
    this.applyData({ categorySlug: data?.categorySlug })
    // @ts-ignore
    this.applyData({ relatedCategoriesBySlug: data?.relatedCategoriesBySlug })
    // @ts-ignore
    this.applyData({ relatedPostsBySlug: data?.relatedPostsBySlug })
    // @ts-ignore
    this.applyData({ recommendedPosts: data?.recommendedPosts })
    // @ts-ignore
    this.applyData({ branchLinksJournal: data?.branchLinksJournal })
    // @ts-ignore
    this.applyData({ branchLinksDating: data?.branchLinksDating })
    // @ts-ignore
    this.applyData({ youtube: data?.youtube })
    // @ts-ignore
    this.applyData({ defaultArticlesBanner: data?.defaultArticlesBanner })
    // @ts-ignore
    this.applyData({ promoBanner: data?.promoBanner })
  }

  serialize = () => {
    return {
      editorChoice: this.editorChoice,
      posts: this.posts,
      pagination: this.pagination,
      postDetails: this.postDetails,
      templates: this.templates,
      enabledYoutube: this.enabledYoutube,
      instagram: this.instagram,
      tiktok: this.tiktok,
      enabledInstagram: this.enabledInstagram,
      categories: this.categories,
      categorySlug: this.categorySlug,
      relatedCategoriesBySlug: this.relatedCategoriesBySlug,
      relatedPostsBySlug: this.relatedPostsBySlug,
      recommendedPosts: this.recommendedPosts,
      branchLinksJournal: this.branchLinksJournal,
      branchLinksDating: this.branchLinksDating,
      youtube: this.youtube,
      defaultArticlesBanner: this.defaultArticlesBanner,
      promoBanner: this.promoBanner,
    }
  }

  getTemplate = (key: string, simpleText = true) => {
    const result = this.templates.find((template) => template.key === key)
    if (result) {
      return simpleText ? htmlToTextTransform(result.template) : result.template
    }

    return ""
  }

  getInitialData = async () => {
    await this.getBlogPage()
    await this.getAllCategories()
    await this.getPosts({ page: 1 })
  }

  getBlogPage = async () => {
    const {
      templates,
      enabledYoutube,
      enabledInstagram,
      branchLinksJournal,
      branchLinksDating,
      promo,
      editorChoice,
      instagrams,
      tiktoks,
      promoBanner,
    } = await this.Providers.BlogProvider.getBlogPage(
      this.Models.GlobalModel.lang,
      this.Models.GlobalModel.isPureDating,
      this.Models.GlobalModel.countryCode,
    )

    this.applyData({
      templates,
      enabledYoutube,
      enabledInstagram,
      branchLinksJournal,
      branchLinksDating,
      promo,
      editorChoice,
      instagram: instagrams,
      tiktok: tiktoks,
      promoBanner,
    })
  }

  getPost = async (slug: string) => {
    if (this.postDetails) return
    try {
      await this.getBlogPage()
      await this.getPostsBySlug(slug)

      const lang = this.Models.GlobalModel.lang
      const post = await this.Providers.BlogProvider.getPost(
        lang,
        slug,
        this.Models.GlobalModel.isPureDating,
        this.Models.GlobalModel.countryCode,
      )

      if (this.isAmp) {
        const { description, elements } = await this.processArticleForAMP(post)
        post.description = description
        post.elements = elements
      }

      return this.applyData({
        postDetails: post,
      })
    } catch (err) {
      const error = err as AxiosError<PostNotFoundHTTPErrorPaylod>
      if (error?.response?.status === 404) {
        if (
          !this.relatedPostsBySlug.length ||
          error?.response?.data?.throw_error
        ) {
          throw err
        }
      }
    }
  }

  getPosts = async ({ page = 1, pageSize = PAGE_SIZE }) => {
    const { data, pagination } = await this.Providers.BlogProvider.getPosts({
      lang: this.Models.GlobalModel.lang,
      page,
      pageSize,
      isPureDating: this.Models.GlobalModel.isPureDating,
      countryCode: this.Models.GlobalModel.countryCode,
    })
    // @ts-ignore
    this.applyData({ posts: this.posts.concat(data), pagination })
  }

  getYoutube = async (max = 12) => {
    if (!this.enabledYoutube) return

    try {
      const youtube: YoutubeVideoModel[] = await this.Providers.BlogProvider.getLastVideos(
        max,
      )
      this.applyData({ youtube })
    } catch {
    } finally {
      this.applyData({ youtube: [] as YoutubeVideoModel[] })
    }
  }

  getPostsByCategory = async (category: string) => {
    if (category) {
      this.applyData({
        categorySlug: category,
      })

      await this.getBlogPage()
      const { posts } = await this.Providers.BlogProvider.getPostsByCategory(
        category,
        this.Models.GlobalModel.lang,
        this.Models.GlobalModel.isPureDating,
        this.Models.GlobalModel.countryCode,
      )
      this.applyData({ posts })
    }

    return
  }

  getPostsBySlug = async (slug: string) => {
    if (slug) {
      try {
        const { posts } = await this.Providers.BlogProvider.getPostsBySlug(
          slug,
          this.Models.GlobalModel.countryCode,
        )
        this.applyData({ relatedPostsBySlug: posts })
      } catch (error) {
        throw error
      }
    }

    return
  }

  getAllCategories = async () => {
    const response = await this.Providers.BlogProvider.getCategories(
      this.Models.GlobalModel.lang,
      this.Models.GlobalModel.isPureDating,
      this.Models.GlobalModel.countryCode,
    )
    this.applyData({ categories: response.data })
  }

  getCategoriesBySlug = async (slug: string) => {
    if (slug) {
      try {
        const {
          categories,
        } = await this.Providers.BlogProvider.getCategoriesBySlug(slug)
        this.applyData({ relatedCategoriesBySlug: categories })
      } catch (error) {
        throw error
      }
    }

    return
  }

  getRecommendedPosts = async (id: string | number) => {
    try {
      const response = await this.Providers.BlogProvider.getRecommendedPosts(
        id,
        this.Models.GlobalModel.isPureDating,
        this.Models.GlobalModel.countryCode,
      )
      this.applyData({
        recommendedPosts: response.data,
      })
    } catch (error) {
      throw error
    }
  }

  getImageSize = async (url: string) => {
    const binary = await this.Providers.MediaProvider.getBinary(url)

    if (binary) {
      const { width = 1, height = 1 } = sizeOf(binary)

      return {
        width,
        height,
      }
    }

    return {
      width: 1,
      height: 1,
    }
  }

  readDefaultArticlesBanner = async () => {
    const { lang } = this.Models.GlobalModel
    try {
      const defaultArticlesBanner = await this.Providers.BlogProvider.readDefaultArticlesBanner(
        lang,
      )

      return this.applyData({
        defaultArticlesBanner,
      })
    } catch (err) {
      if ((err as AxiosError).response) {
        const error = err as AxiosError
        console.error(error.response) // eslint-disable-line no-console
      }
    }
  }

  processArticleForAMP = async (post: PostModel) => {
    try {
      const description = parse(post.description)
      const images = Array.from(description.querySelectorAll("img"))
      for await (const image of images) {
        const src = image.getAttribute("src")
        if (src) {
          const { width, height } = await this.getImageSize(src)
          image.setAttribute("width", width.toString())
          image.setAttribute("height", height.toString())
        }
      }

      const elements = [] as Element[]

      for await (const element of post.elements) {
        const root = parse(element.content)
        const images = Array.from(root.querySelectorAll("img"))
        for await (const image of images) {
          const src = image.getAttribute("src")
          if (src) {
            const { width, height } = await this.getImageSize(src)
            image.setAttribute("width", width.toString())
            image.setAttribute("height", height.toString())
          }
        }
        elements.push({
          background: element.background,
          content: root.toString(),
        })
      }

      return {
        description: description.toString(),
        elements,
      }
    } catch (err) {
      return {
        description: post.description,
        elements: post.elements,
      }
    }
  }
}

export interface PostNotFoundHTTPErrorPaylod {
  status: number
  title: string
  details: string
  throw_error: boolean
  meta: {
    posts: PostModel[]
  }
}
